import { summarizeTc6Result } from '../tc6/tc6Domain';
import { summarizeTugResult } from '../tug/tugDomain';
import { summarizeGaitSpeedResult } from '../gait-speed/gaitSpeedDomain';
import { summarizeStsResult, type StsTestKey } from '../sts/stsDomain';
import { summarizeHandgripResult, type HandgripResultPayload } from '../handgrip/handgripDomain';
import {
  summarizePimaxPayload,
  summarizePemaxPayload,
  RESPIRATORY_POSITION_LABELS,
  type PimaxResultPayload,
  type PemaxResultPayload,
  type RespiratoryTestPosition,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import { summarizePeakFlowPayload } from '../peak-flow/peakFlowDomain';
import {
  normalizePeakCoughFlowPayload,
  summarizePeakCoughFlowPayload,
  PEAK_COUGH_FLOW_POSITIONS,
} from '../peak-cough-flow/peakCoughFlowDomain';
import {
  summarizePosturalVitalCapacityPayload,
  summarizeSnipPayload,
} from '../respiratory-muscle/neuromuscularDomain';
import type { AssessmentResultV2 } from '../types';
import type { AssessmentReportSourceSnapshotV2 } from './assessmentReportDomain';

export type AssessmentReportDocumentModel = 'summary' | 'initial' | 'final' | 'longitudinal' | 'findings';

export const ASSESSMENT_REPORT_DOCUMENT_MODELS: ReadonlyArray<{
  value: AssessmentReportDocumentModel; label: string;
}> = [
  { value: 'summary', label: 'Resumo das avaliações' },
  { value: 'initial', label: 'Avaliação inicial' },
  { value: 'final', label: 'Avaliação final / alta' },
  { value: 'longitudinal', label: 'Relatório longitudinal / pré–pós' },
  { value: 'findings', label: 'Achados físico-funcionais e diagnóstico' },
];

export const isAssessmentReportDocumentModel = (value: unknown): value is AssessmentReportDocumentModel =>
  ASSESSMENT_REPORT_DOCUMENT_MODELS.some((item) => item.value === value);

/** Numeric observations captured with the source, never recalculated from today's patient record. */
export interface AssessmentLongitudinalMeasureSnapshot {
  key: string;
  label: string;
  value: number;
  unit: string;
  seriesLabel: string | null;
  protocolKey: string | null;
  referenceValue: number | null;
  referenceLabel: string | null;
  percentPredicted: number | null;
  lowerIsBetter: boolean | null;
  allowPercentChange: boolean;
}

export interface AssessmentLongitudinalPoint extends AssessmentLongitudinalMeasureSnapshot {
  resultId: string;
  assessmentDate: string;
  timestamp: number;
  sourceUpdatedAt: string | null;
  professionalName: string | null;
}

export interface AssessmentLongitudinalSeries {
  key: string;
  testKey: string;
  testLabel: string;
  measureLabel: string;
  seriesLabel: string | null;
  unit: string;
  points: AssessmentLongitudinalPoint[];
  comparable: boolean;
  comparisonNote: string | null;
  delta: number | null;
  deltaPercent: number | null;
  direction: 'increase' | 'decrease' | 'unchanged' | null;
}

type Data = Record<string, unknown>;
const record = (value: unknown): Data => value && typeof value === 'object' && !Array.isArray(value) ? value as Data : {};
const number = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;
const text = (value: unknown): string => typeof value === 'string' ? value.trim() : '';
const positive = (value: unknown): number | null => { const n = number(value); return n != null && n > 0 ? n : null; };
const percent = (value: number, denominator: number | null): number | null =>
  denominator != null && denominator > 0 ? value / denominator * 100 : null;

export const formatLongitudinalNumber = (value: number, maximumFractionDigits = 2): string =>
  value.toLocaleString('pt-BR', { maximumFractionDigits });

export const formatLongitudinalDate = (value: string): string => {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? 'Data não informada' : date.toLocaleDateString('pt-BR');
};

export const normalizeAssessmentLongitudinalMeasure = (value: unknown): AssessmentLongitudinalMeasureSnapshot | null => {
  const item = record(value);
  const measured = number(item.value);
  if (!text(item.key) || !text(item.label) || !text(item.unit) || measured == null) return null;
  return {
    key: text(item.key), label: text(item.label), value: measured, unit: text(item.unit),
    seriesLabel: text(item.seriesLabel) || null,
    protocolKey: text(item.protocolKey) || null,
    referenceValue: positive(item.referenceValue),
    referenceLabel: text(item.referenceLabel) || null,
    percentPredicted: number(item.percentPredicted),
    lowerIsBetter: typeof item.lowerIsBetter === 'boolean' ? item.lowerIsBetter : null,
    allowPercentChange: item.allowPercentChange === true,
  };
};

const observation = (
  key: string, label: string, value: number | null, unit: string,
  options: Partial<Omit<AssessmentLongitudinalMeasureSnapshot, 'key' | 'label' | 'value' | 'unit'>> = {},
): AssessmentLongitudinalMeasureSnapshot[] => value == null ? [] : [{
  key, label, value, unit, seriesLabel: null, protocolKey: null,
  referenceValue: null, referenceLabel: null, percentPredicted: null,
  lowerIsBetter: null, allowPercentChange: true, ...options,
}];

const pressureMeasures = (testKey: 'PIMAX' | 'PEMAX', payload: Data): AssessmentLongitudinalMeasureSnapshot[] => {
  const suppliedSeries = Array.isArray(payload.positionSeries) ? payload.positionSeries : [];
  if (suppliedSeries.length) {
    return suppliedSeries.flatMap((entry) => {
      const series = record(entry);
      if (text(series.notPerformedReason)) return [];
      const seriesResult = record(series.result);
      return pressureMeasures(testKey, {
        ...seriesResult,
        positionSeries: undefined,
        positionProtocol: { ...record(series.technique), position: series.position,
          angleDegrees: series.angleDegrees, otherPosition: series.otherPosition },
      });
    });
  }
  const summary = testKey === 'PIMAX'
    ? summarizePimaxPayload(payload as unknown as PimaxResultPayload)
    : summarizePemaxPayload(payload as unknown as PemaxResultPayload);
  const protocol = record(payload.positionProtocol);
  const position = text(protocol.position) || text(payload.position) || 'unknown';
  const positionLabel = RESPIRATORY_POSITION_LABELS[position as RespiratoryTestPosition]
    ?? (position === 'standing' ? 'Em pé' : 'Posição não informada');
  const initialVolume = text(protocol.initialVolume);
  const pressureInterface = text(protocol.interface);
  const positionDescription = position === 'other' ? text(protocol.otherPosition) : '';
  const angleDescription = number(protocol.angleDegrees) != null ? `${formatLongitudinalNumber(number(protocol.angleDegrees)!)}°` : '';
  const protocolDescription = [positionDescription, angleDescription,
    initialVolume && initialVolume !== 'unknown' ? initialVolume : '',
    pressureInterface === 'mouthpiece' ? 'Bocal' : pressureInterface === 'mask' ? 'Máscara' : '',
    text(protocol.device)].filter(Boolean).join(' · ');
  const hasProtocol = position !== 'unknown' && position !== 'unspecified'
    && Boolean(initialVolume) && initialVolume !== 'unknown'
    && Boolean(pressureInterface) && pressureInterface !== 'unknown';
  const technique = hasProtocol ? JSON.stringify([
    position, number(protocol.angleDegrees), text(protocol.otherPosition), initialVolume, pressureInterface,
    text(protocol.device), protocol.facialSupport === true, Boolean(payload.breathelink),
  ]) : null;
  // Use only the predicted value saved for this observation, including its applicability.
  const referenceApplicable = testKey !== 'PIMAX' || !('referenceApplicable' in summary) || summary.referenceApplicable !== false;
  const predicted = referenceApplicable ? positive(payload.predicted) : null;
  const measured = number(summary.max);
  return observation('maximum_pressure', testKey === 'PIMAX' ? 'PImax' : 'PEmax', measured, 'cmH2O', {
    seriesLabel: [positionLabel, protocolDescription].filter(Boolean).join(' · '), protocolKey: technique, lowerIsBetter: false,
    referenceValue: predicted,
    referenceLabel: predicted == null ? null : text(payload.referenceLabel) || 'Valor previsto registrado na avaliação',
    percentPredicted: predicted != null && measured != null
      ? number(payload.percentPredicted) ?? percent(measured, predicted) : null,
  });
};

/** Extract canonical measurements, retaining only references actually persisted at source time. */
export const extractAssessmentLongitudinalMeasures = (result: AssessmentResultV2): AssessmentLongitudinalMeasureSnapshot[] => {
  if (result.status !== 'completed') return [];
  const payload = record(result.payloadJson);
  if (result.testKey === 'PIMAX' || result.testKey === 'PEMAX') return pressureMeasures(result.testKey, payload);
  if (result.testKey === 'TC6') {
    const summary = summarizeTc6Result(payload);
    const attempt = record(summary.bestAttempt === 2 ? payload.attempt2 : payload.attempt1);
    const derived = record(attempt.derivedMetrics);
    const predicted = positive(derived.predictedDistance_m);
    const distance = number(summary.bestDistance_m);
    const protocol = record(payload.protocol);
    const corridor = positive(attempt.corridorLength_m);
    const technique = corridor != null ? JSON.stringify([
      corridor, text(protocol.walkingAid), protocol.oxygenUsed ?? null, protocol.oxygenFlowLMin ?? null,
      // Legacy TC6 does not capture oxygen/device consistently, so require an explicit protocol.
      text(protocol.protocolKey),
    ]) : null;
    const sufficientlyDocumented = technique && Object.keys(protocol).length > 0;
    return observation('distance', 'Distância percorrida', distance, 'm', {
      lowerIsBetter: false, protocolKey: sufficientlyDocumented ? technique : null,
      referenceValue: predicted,
      referenceLabel: predicted != null ? text(derived.predictedFormula) || text(payload.referenceEquation) || 'Referência registrada' : null,
      percentPredicted: predicted != null && distance != null ? number(derived.percentPredicted) ?? percent(distance, predicted) : null,
    });
  }
  if (result.testKey === 'TUG') {
    const summary = summarizeTugResult(payload);
    const protocol = record(payload.protocol);
    const attempt = record(summary.bestAttemptPayload);
    const reference = record(payload.referenceComparison);
    const predicted = positive(reference.predictedTimeSeconds);
    return observation('time', 'Tempo', number(summary.bestTimeSeconds), 'segundos', {
      lowerIsBetter: true,
      seriesLabel: [summary.walkingAidLabel, summary.physicalAssistance ? 'Com assistência física' : 'Sem assistência física'].filter(Boolean).join(' · '),
      protocolKey: Object.keys(protocol).length > 0 ? JSON.stringify([
        number(protocol.chairHeightCm), number(protocol.distanceMeters),
        text(attempt.walkingAid) || text(payload.walkingAid), summary.physicalAssistance,
      ]) : null,
      referenceValue: predicted, referenceLabel: predicted != null ? text(reference.source) || 'Referência registrada' : null,
      percentPredicted: predicted != null ? number(reference.percentPredicted) : null,
    });
  }
  if (result.testKey === '4MGS') {
    const summary = summarizeGaitSpeedResult(payload);
    const protocol = record(payload.protocol);
    const attempt = record(summary.bestAttemptPayload);
    const reference = record(payload.referenceComparison);
    const predicted = positive(reference.predictedSpeedMs);
    return observation('speed', 'Velocidade da marcha', number(summary.bestSpeedMs), 'm/s', {
      lowerIsBetter: false,
      seriesLabel: [summary.walkingAidLabel, summary.oxygenUsed ? 'Com oxigênio' : 'Sem oxigênio registrado',
        summary.physicalAssistance ? 'Com assistência física' : 'Sem assistência física'].join(' · '),
      protocolKey: Object.keys(protocol).length > 0 ? JSON.stringify([
        protocol.distanceMeters, protocol.pace, protocol.staticStart, protocol.stopwatchStartsOnMovement,
        protocol.stopwatchStopsOnFirstFootPastCone, text(attempt.walkingAid), summary.physicalAssistance,
        summary.oxygenUsed, number(attempt.oxygenFlowLMin),
      ]) : null,
      referenceValue: predicted, referenceLabel: predicted != null ? text(reference.source) || 'Referência registrada' : null,
      percentPredicted: predicted != null ? number(reference.percentPredicted) : null,
    });
  }
  if (['5xSTS', 'STS_30S', 'STS_1MIN'].includes(result.testKey)) {
    const summary = summarizeStsResult(result.testKey as StsTestKey, payload);
    const reference = record(payload.referenceComparison);
    const predicted = positive(reference.predictedValue);
    return observation('performance', summary.measurementLabel, number(summary.best),
      result.testKey === '5xSTS' ? 'segundos' : 'repetições', {
        lowerIsBetter: summary.lowerIsBetter,
        seriesLabel: [summary.chairHeightCm != null ? `Cadeira: ${formatLongitudinalNumber(summary.chairHeightCm)} cm` : 'Altura da cadeira não informada',
          summary.usedArms == null ? 'Uso dos braços não informado' : summary.usedArms ? 'Com uso dos braços' : 'Sem uso dos braços'].join(' · '),
        protocolKey: summary.chairHeightCm != null && summary.usedArms != null
          ? JSON.stringify([summary.chairHeightCm, summary.usedArms]) : null,
        referenceValue: predicted, referenceLabel: predicted != null ? text(reference.source) || 'Referência registrada' : null,
        percentPredicted: predicted != null ? number(reference.percentPredicted) : null,
      });
  }
  if (result.testKey === 'HANDGRIP') {
    const summary = summarizeHandgripResult(payload as unknown as HandgripResultPayload);
    const dominant = text(payload.dominantHand);
    return (['right', 'left'] as const).flatMap((side) => {
      const value = side === 'right' ? number(summary.bestRight) : number(summary.bestLeft);
      const label = side === 'right' ? 'Mão direita' : 'Mão esquerda';
      const dominanceLabel = dominant ? side === dominant ? 'dominante' : 'não dominante' : 'dominância não informada';
      // A shared overall P50 must never be silently attributed to an individual hand.
      return observation(`handgrip_${side}`, 'Força de preensão palmar', value, text(payload.unit) || 'kg', {
        lowerIsBetter: false, seriesLabel: `${label} (${dominanceLabel})`,
        protocolKey: JSON.stringify([result.testVersion, side, dominant || null]),
      });
    });
  }
  if (result.testKey === 'PEAK_FLOW') {
    const summary = summarizePeakFlowPayload(payload);
    const predicted = summary.referenceMode === 'predicted_reference' ? positive(payload.referenceValue) : null;
    return [
      ...observation('pre_flow', 'Pico de fluxo expiratório', number(summary.bestPreFlow), 'L/min', {
        lowerIsBetter: false, seriesLabel: summary.bronchodilatorUsed ? 'Pré-broncodilatador' : 'Sem broncodilatador registrado',
        protocolKey: JSON.stringify([result.testVersion, 'pre', summary.bronchodilatorUsed]),
        referenceValue: predicted,
        referenceLabel: positive(payload.referenceValue) != null ? text(payload.referenceSourceLabel) || text(payload.referenceLabel) || summary.referenceLabel : null,
        percentPredicted: predicted != null ? number(summary.referencePercentPre) : null,
      }),
      ...observation('post_flow', 'Pico de fluxo expiratório', number(summary.bestPostFlow), 'L/min', {
        lowerIsBetter: false, seriesLabel: 'Pós-broncodilatador',
        protocolKey: text(payload.bronchodilatorMedication) && number(payload.bronchodilatorWaitMinutes) != null
          ? JSON.stringify([result.testVersion, text(payload.bronchodilatorMedication), payload.bronchodilatorWaitMinutes]) : null,
        referenceValue: predicted,
        referenceLabel: predicted != null ? text(payload.referenceSourceLabel) || summary.referenceLabel : null,
        percentPredicted: predicted != null ? number(summary.referencePercentPost) : null,
      }),
    ];
  }
  if (result.testKey === 'PEAK_COUGH_FLOW') {
    const normalized = normalizePeakCoughFlowPayload(payload);
    const summary = summarizePeakCoughFlowPayload(payload);
    return summary.series.flatMap((item) => {
      const techniqueNotes = normalized?.series.find((entry) => entry.condition === item.condition)?.techniqueNotes;
      const assistanceKnown = item.condition === 'unassisted' || Boolean(text(techniqueNotes));
      const inclinationKnown = normalized?.position !== 'semi_recumbent' || normalized.inclinationDegrees != null;
      return observation(`cough_flow_${item.condition}`, 'Pico de fluxo de tosse', item.bestFlow, 'L/min', {
        lowerIsBetter: false,
        seriesLabel: [item.label, normalized?.position ? PEAK_COUGH_FLOW_POSITIONS[normalized.position] : 'Posição não informada'].join(' · '),
        protocolKey: assistanceKnown && inclinationKnown && normalized?.position && normalized.position !== 'other' && normalized.interface && normalized.device
          ? JSON.stringify([item.condition, normalized.position, normalized.inclinationDegrees, normalized.interface,
            normalized.device.trim().toLowerCase(), text(techniqueNotes)])
          : null,
      });
    });
  }
  if (result.testKey === 'CV_POSTURAL') {
    const summary = summarizePosturalVitalCapacityPayload(payload);
    const known = ['slow', 'forced'].includes(text(payload.maneuver))
      && Boolean(text(payload.interface)) && Boolean(text(payload.device)) && typeof payload.abdominalSupport === 'boolean';
    return (['seated', 'supine'] as const).flatMap((position) => observation(`vital_capacity_${position}`,
      payload.maneuver === 'forced' ? 'Capacidade vital forçada' : 'Capacidade vital lenta',
      position === 'seated' ? summary.bestSeated : summary.bestSupine, 'L', {
        lowerIsBetter: false, seriesLabel: position === 'seated' ? 'Sentado' : 'Decúbito dorsal',
        protocolKey: known ? JSON.stringify([position, payload.maneuver, payload.interface,
          text(payload.device).toLowerCase(), payload.abdominalSupport]) : null,
      }));
  }
  if (result.testKey === 'SNIP') {
    const summary = summarizeSnipPayload(payload);
    const position = text(payload.position);
    const known = ['seated', 'supine', 'semi_recumbent', 'other'].includes(position)
      && Boolean(text(payload.device)) && payload.initialVolume === 'FRC'
      && Boolean(text(payload.contralateralNostril)) && typeof payload.mouthClosed === 'boolean'
      && (position !== 'semi_recumbent' || number(payload.angleDegrees) != null)
      && (position !== 'other' || Boolean(text(payload.otherPosition)));
    return (['left', 'right'] as const).flatMap((side) => observation(`snip_${side}`, 'Pressão inspiratória nasal',
      side === 'left' ? summary.bestLeft : summary.bestRight, 'cmH2O', {
        lowerIsBetter: false,
        seriesLabel: [side === 'left' ? 'Narina esquerda' : 'Narina direita',
          RESPIRATORY_POSITION_LABELS[position as RespiratoryTestPosition] || 'Posição não informada'].join(' · '),
        protocolKey: known ? JSON.stringify([side, position, number(payload.angleDegrees), text(payload.otherPosition),
          text(payload.device).toLowerCase(), payload.initialVolume, payload.contralateralNostril, payload.mouthClosed]) : null,
      }));
  }
  const questionnaires: Record<string, { label: string; value: number | null; minimum: number; maximum: number; lower: boolean; unit: string }> = {
    CAT: { label: 'Escore CAT', value: number(payload.total), minimum: 0, maximum: 40, lower: true, unit: 'pontos' },
    ACT: { label: 'Escore ACT', value: number(payload.total), minimum: 5, maximum: 25, lower: false, unit: 'pontos' },
    mMRC: { label: 'Grau mMRC', value: number(payload.grade), minimum: 0, maximum: 4, lower: true, unit: 'grau' },
    SGRQ: { label: 'Escore total SGRQ', value: number(payload.total), minimum: 0, maximum: 100, lower: true, unit: 'pontos' },
    AQLQ: { label: 'Escore global AQLQ', value: number(payload.total), minimum: 1, maximum: 7, lower: false, unit: 'pontos' },
  };
  const questionnaire = questionnaires[result.testKey];
  if (questionnaire) {
    const measured = questionnaire.value;
    if (measured == null || measured < questionnaire.minimum || measured > questionnaire.maximum) return [];
    return observation('score', questionnaire.label, measured, questionnaire.unit, {
      lowerIsBetter: questionnaire.lower, allowPercentChange: false,
      protocolKey: JSON.stringify([result.testKey, result.testVersion]),
    });
  }
  return [];
};

/** Preserve the clinical sections while avoiding recursive copies of selected source results. */
export const captureClinicalAssessmentSnapshot = (value: unknown): Record<string, unknown> | undefined => {
  const payload = record(value);
  const keys = ['schemaVersion', 'assessmentMoment', 'episodeLabel', 'baselineResultId', 'assessmentReason',
    'referralDiagnosis', 'currentClinicalCondition', 'mainSymptoms', 'functionalLimitations', 'assistiveDevices',
    'precautions', 'patientGoals', 'previousFunction', 'physicalExamination', 'externalExams', 'measurableGoals',
    'professionalSynthesis', 'initialPlan', 'additionalNotes', 'prognosis', 'followUpPlan', 'closureReason',
    'anamnesisSnapshot', 'finishedAt'];
  if (!keys.some((key) => payload[key] != null)) return undefined;
  const captured: Record<string, unknown> = {};
  for (const key of keys) {
    if (payload[key] !== undefined) captured[key] = JSON.parse(JSON.stringify(payload[key]));
  }
  return captured;
};

export const buildAssessmentLongitudinalSeries = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): AssessmentLongitudinalSeries[] => {
  const groups = new Map<string, AssessmentLongitudinalSeries>();
  for (const source of sources) {
    if (source.snapshot.sourceStatus !== 'completed' || source.validity.status === 'invalid') continue;
    const timestamp = Date.parse(source.snapshot.assessmentDate);
    if (!Number.isFinite(timestamp)) continue;
    for (const candidate of source.snapshot.longitudinalMeasures ?? []) {
      const measure = normalizeAssessmentLongitudinalMeasure(candidate);
      if (!measure) continue;
      const key = JSON.stringify([source.snapshot.testKey, source.snapshot.testVersion, measure.key,
        measure.unit, measure.seriesLabel, measure.protocolKey]);
      let series = groups.get(key);
      if (!series) {
        series = { key, testKey: source.snapshot.testKey, testLabel: source.snapshot.testLabel,
          measureLabel: measure.label, seriesLabel: measure.seriesLabel, unit: measure.unit,
          points: [], comparable: false, comparisonNote: null,
          delta: null, deltaPercent: null, direction: null };
        groups.set(key, series);
      }
      if (series.points.some((point) => point.resultId === source.resultId)) continue;
      series.points.push({ ...measure, resultId: source.resultId,
        assessmentDate: source.snapshot.assessmentDate, timestamp,
        sourceUpdatedAt: source.snapshot.sourceUpdatedAt,
        professionalName: source.professional.name });
    }
  }
  return [...groups.values()].map((series) => {
    series.points.sort((a, b) => a.timestamp - b.timestamp || a.resultId.localeCompare(b.resultId));
    const first = series.points[0];
    const last = series.points[series.points.length - 1];
    const distinctDates = new Set(series.points.map((point) => point.timestamp)).size;
    series.comparable = series.points.length >= 2 && distinctDates >= 2 && Boolean(first.protocolKey)
      && series.points.every((point) => point.lowerIsBetter === first.lowerIsBetter);
    series.comparisonNote = series.points.length < 2 ? 'Medida pontual; ainda não há comparação longitudinal.'
      : distinctDates < 2 ? 'Medidas do mesmo momento; não representam mudança ao longo do tempo.'
        : !first.protocolKey ? 'Protocolo ou condição de aplicação incompleto; diferença automática não calculada.'
          : !series.comparable ? 'Condições de comparação incompatíveis.' : null;
    if (series.comparable) {
      series.delta = last.value - first.value;
      series.deltaPercent = first.allowPercentChange && last.allowPercentChange && first.value > 0
        ? series.delta / first.value * 100 : null;
      series.direction = series.delta > 0 ? 'increase' : series.delta < 0 ? 'decrease' : 'unchanged';
    }
    return series;
  }).sort((a, b) => a.testLabel.localeCompare(b.testLabel, 'pt-BR') || a.key.localeCompare(b.key));
};
